import type {
  CountRequest,
  CountResponse,
  FilterNode,
  GroupedSearchResponse,
  Page,
  SearchRequestInput,
  SearchResponse,
  SearchResult,
  TicketListItem,
  View,
} from '@servicetim/shared';
import { type QueryKey, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { portalPath, useApiContext } from './client';
import { DEFAULT_PAGE_SIZE, pageQuery, usePagedList } from './paging';

const searchPath = portalPath('/tickets/search');
const countPath = portalPath('/tickets/count');
const viewsBase = portalPath('/views');

export const isGroupedSearch = (result: SearchResult): result is GroupedSearchResponse =>
  'groups' in result;

/**
 * `POST /v1/tickets/search` without `groupBy`, paged through `page.cursor`. Pass `undefined`
 * to keep the query idle (e.g. until a view is chosen).
 */
export function useTicketSearch(request: SearchRequestInput | undefined) {
  const { request: send, keys } = useApiContext();
  const body = useMemo(() => (request ? { ...request, page: undefined } : undefined), [request]);
  const pageSize = request?.page?.size ?? DEFAULT_PAGE_SIZE;
  const query = useInfiniteQuery({
    queryKey: keys.search.results({ ...body, pageSize }),
    queryFn: ({ pageParam, signal }) =>
      send<SearchResponse>('POST', searchPath, {
        body: { ...body, page: { size: pageSize, cursor: pageParam } },
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: request !== undefined,
  });
  const items = useMemo<TicketListItem[]>(
    () => query.data?.pages.flatMap((p) => p.items) ?? [],
    [query.data],
  );
  return {
    ...query,
    items,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}

/** `POST /v1/tickets/search` with `groupBy` (board columns). */
export function useGroupedTicketSearch(request: SearchRequestInput | undefined) {
  const { request: send, keys } = useApiContext();
  return useQuery({
    queryKey: [...keys.search.results(request), 'grouped'] as QueryKey,
    queryFn: ({ signal }) =>
      send<GroupedSearchResponse>('POST', searchPath, { body: request, signal }),
    enabled: request !== undefined && request.groupBy !== undefined,
  });
}

/** `POST /v1/tickets/count` (exact up to 10 000). */
export function useTicketCount(
  filter: FilterNode | undefined,
  options: { enabled?: boolean } = {},
) {
  const { request, keys } = useApiContext();
  return useQuery({
    queryKey: keys.search.count(filter),
    queryFn: ({ signal }) =>
      request<CountResponse>('POST', countPath, {
        body: { filter } satisfies CountRequest,
        signal,
      }),
    enabled: options.enabled ?? true,
  });
}

/** One-shot search (pickers, lookups). */
export function useSearchTickets() {
  const { request } = useApiContext();
  return useMutation({
    mutationFn: (body: SearchRequestInput) => request<SearchResponse>('POST', searchPath, { body }),
  });
}

/* ----------------------------- portal views ----------------------------- */

/** `GET /v1/views?projectId=&mine=`: views the caller may run (shared + own). */
export function usePortalViews(
  filters: { projectId?: string; mine?: boolean } = {},
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const { request, keys } = useApiContext();
  return usePagedList<View>({
    queryKey: [...keys.views.list({ ...filters, pageSize }), 'portal'],
    fetchPage: (cursor, signal) =>
      request<Page<View>>('GET', viewsBase, {
        query: {
          projectId: filters.projectId,
          mine: filters.mine === undefined ? undefined : String(filters.mine),
          ...pageQuery(cursor, pageSize),
        },
        signal,
      }),
  });
}

/**
 * `POST /v1/views/{id}/run`: the stored AST with `$me` resolved. Grouped views (board) return
 * one page of groups; list/table views page through `cursor`.
 */
export function useRunView(viewId: string | undefined) {
  const { request, keys } = useApiContext();
  const query = useInfiniteQuery({
    queryKey: keys.views.run(viewId ?? ''),
    queryFn: ({ pageParam, signal }) =>
      request<SearchResult>('POST', `${viewsBase}/${viewId}/run`, {
        body: pageParam ? { cursor: pageParam } : {},
        signal,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: viewId !== undefined,
  });
  const first = query.data?.pages[0];
  const items = useMemo<TicketListItem[]>(
    () =>
      query.data?.pages.flatMap((p) =>
        isGroupedSearch(p) ? p.groups.flatMap((g) => g.items) : p.items,
      ) ?? [],
    [query.data],
  );
  return {
    ...query,
    items,
    groups: first && isGroupedSearch(first) ? first.groups : undefined,
    hasMore: query.hasNextPage,
    isLoadingMore: query.isFetchingNextPage,
    loadMore: () => {
      if (query.hasNextPage && !query.isFetchingNextPage) void query.fetchNextPage();
    },
  };
}
